"use client";

import type { CSSProperties } from "react";
import { useCallback, useState } from "react";
import { Box } from "@mui/material";
import svgPaths from "../header/dathacons/svg-bvmy9onrap";
import { Carousel } from "src/lib/components/salify-next-layout/default/components/carousel";
import { COMMON_DOT_STYLES } from "src/lib/components/salify-next-layout/default/components/carousel/styles";
import { toAssetImages } from "src/lib/utils";

export interface BannerWrapperDathaconsFeature {
  label: string;
  icon: "idea" | "tech" | "heart" | "sustainable";
}

export interface BannerWrapperDathaconsImageVariant {
  src: string;
  width: number;
}

export interface BannerWrapperDathaconsImage {
  src: string;
  width?: number;
  variants?: BannerWrapperDathaconsImageVariant[];
}

export type BannerWrapperDathaconsImageInput = string | BannerWrapperDathaconsImage;

export interface BannerWrapperDathaconsProps {
  imgSrc?: string;
  images?: BannerWrapperDathaconsImageInput[];
  title?: string;
  subtitle?: string;
  features?: BannerWrapperDathaconsFeature[];
  animationEnabled?: boolean;
  autoplayInterval?: number;
}

function IdeaIcon() {
  return (
    <div className="absolute inset-[6.25%_10.94%]" data-name="Idea">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 50 56"
      >
        <g>
          <path d={svgPaths.p2a1645c0} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.pfade000} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p13294f00} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p73afa00} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p25503200} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p1daa0780} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p3dd48780} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p139b7e00} fill="var(--fill-0, #FFA411)" />
          <path d="M17 44H33V46H17V44Z" fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p2731b680} fill="var(--fill-0, #FFA411)" />
        </g>
      </svg>
    </div>
  );
}

function TechIcon() {
  return (
    <div className="relative shrink-0 size-[63px]">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 63 63"
      >
        <g>
          <path
            clipRule="evenodd"
            d={svgPaths.p1ee49180}
            fill="var(--fill-0, #FFA411)"
            fillRule="evenodd"
          />
        </g>
      </svg>
    </div>
  );
}

function Group5() {
  return (
    <div className="absolute inset-[8.46%_0]">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 64 53.1666"
      >
        <g>
          <path d={svgPaths.p7446620} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p22a2ee80} fill="var(--fill-0, #FFA411)" />
          <path d={svgPaths.p3aa5bf00} fill="var(--fill-0, #FFA411)" />
        </g>
      </svg>
    </div>
  );
}

function HeartHandsIcon() {
  return (
    <div className="overflow-clip relative shrink-0 size-[64px]">
      <div className="relative size-full">
        <Group5 />
      </div>
    </div>
  );
}

function SustainableIcon() {
  return (
    <div className="relative shrink-0 size-[64px]">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 64 64"
      >
        <g>
          <path
            clipRule="evenodd"
            d={svgPaths.p34126100}
            fill="var(--fill-0, #FFA411)"
            fillRule="evenodd"
          />
        </g>
      </svg>
    </div>
  );
}

function FeatureIcon({ icon }: { icon: BannerWrapperDathaconsFeature["icon"] }) {
  switch (icon) {
    case "idea":
      return (
        <div className="overflow-clip relative shrink-0 size-[64px]">
          <IdeaIcon />
        </div>
      );
    case "tech":
      return <TechIcon />;
    case "heart":
      return <HeartHandsIcon />;
    case "sustainable":
      return <SustainableIcon />;
    default:
      return null;
  }
}

const cardWidths = ["w-[258px]", "w-[257px]", "w-[258px]", "w-[258px]"];

function ValueCard({
  label,
  icon,
  widthClass,
}: {
  label: string;
  icon: BannerWrapperDathaconsFeature["icon"];
  widthClass: string;
}) {
  return (
    <div
      className={`bg-white content-stretch flex flex-col h-[160px] items-start overflow-clip p-[20px] relative rounded-[12px] shrink-0 ${widthClass} max-w-[min(258px,22vw)] sm:max-w-none min-w-0 flex-1 sm:flex-none`}
      data-name="Icon container"
    >
      <div className="content-stretch flex flex-col gap-[16px] items-center relative shrink-0 w-full">
        <FeatureIcon icon={icon} />
        <div className="flex flex-col font-semibold justify-center leading-[normal] min-w-full not-italic relative shrink-0 text-[#303030] text-[18px] md:text-[20px] text-center w-[min-content] max-w-full">
          <p className="leading-[normal]">{label}</p>
        </div>
      </div>
    </div>
  );
}

/** Sticky header (top bar + nav), px — measure on dathacons header. */
const DATHACONS_HEADER_HEIGHT_PX = 132;
/** Feature cards overlap into the hero fold. */
const HERO_CARD_OVERLAP_PX = 83;

/** Hero graphic banners are 960×415. */
const HERO_BANNER_ASPECT = 415 / 960;

const graphicHeroFrameStyle: CSSProperties = {
  width: "100%",
  height: `min(calc(100vw * ${HERO_BANNER_ASPECT}), calc(100svh - ${DATHACONS_HEADER_HEIGHT_PX}px))`,
};

function heroImageUrl(src: string) {
  return toAssetImages(src);
}

function normalizeHeroImage(input: BannerWrapperDathaconsImageInput): BannerWrapperDathaconsImage {
  if (typeof input === "string") {
    if (!input.endsWith(".jpg")) return { src: input };
    const stem = input.replace(/\.jpg$/, "");
    return {
      src: input,
      width: 1920,
      variants: [
        { src: `${stem}-768.jpg`, width: 768 },
        { src: `${stem}-1280.jpg`, width: 1280 },
      ],
    };
  }
  return input;
}

function heroImageSrcSet(image: BannerWrapperDathaconsImage): string | undefined {
  const entries = [...(image.variants ?? []), { src: image.src, width: image.width ?? 1920 }].sort(
    (a, b) => a.width - b.width,
  );

  if (entries.length <= 1) return undefined;

  return entries.map((entry) => `${heroImageUrl(entry.src)} ${entry.width}w`).join(", ");
}

function HeroBannerImage({
  image,
  priority = false,
}: {
  image: BannerWrapperDathaconsImage;
  graphic?: boolean;
  priority?: boolean;
}) {
  const url = heroImageUrl(image.src);
  const srcSet = heroImageSrcSet(image);
  return (
    <img
      alt=""
      src={url}
      srcSet={srcSet}
      sizes="100vw"
      decoding="async"
      fetchPriority={priority ? "high" : undefined}
      loading={priority ? "eager" : "lazy"}
      className="block h-full w-full object-cover object-center dathacons-hero-slide-img"
    />
  );
}

function HeroSlideImage({
  image,
  priority,
}: {
  image: BannerWrapperDathaconsImage;
  graphic?: boolean;
  priority?: boolean;
}) {
  return (
    <div className="relative h-full w-full">
      <HeroBannerImage image={image} priority={priority} />
    </div>
  );
}

function HeroCarousel({
  images,
  graphic,
  animationEnabled,
  autoplayInterval,
  onReady,
}: {
  images: BannerWrapperDathaconsImage[];
  graphic: boolean;
  animationEnabled: boolean;
  autoplayInterval: number;
  onReady?: () => void;
}) {
  const shouldAnimate = animationEnabled && images.length > 1;

  if (!shouldAnimate) {
    return null;
  }

  return (
    <Box
      className="dathacons-hero-carousel"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        height: "100%",
        overflow: "hidden",
        "& > div": {
          height: "100%",
        },
        "& .slick-slider": {
          height: "100%",
          overflow: "hidden",
        },
        "& .slick-list": {
          height: "100%",
          overflow: "hidden",
        },
        "& .slick-track": {
          height: "100%",
        },
        "& .slick-slider.slick-fade .slick-track": {
          display: "block",
          height: "100%",
        },
        "& .slick-slider.slick-fade .slick-slide": {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          float: "none",
        },
        "& .slick-slide": {
          height: "100%",
        },
        "& .slick-slide > div": {
          position: "relative",
          height: "100%",
        },
        "& .slick-dots": { bottom: 16, zIndex: 2 },
      }}
    >
      <Carousel
        dots
        arrows={false}
        arrowStyles={undefined}
        slidesToShow={1}
        slidesToScroll={1}
        infinite
        autoplay
        autoplaySpeed={autoplayInterval}
        speed={900}
        fade
        onInit={onReady}
        dotStyles={COMMON_DOT_STYLES as any}
        dotColor="#FFA411"
      >
        {images.map((image, index) => (
          <div key={`${image.src}-${index}`}>
            <HeroSlideImage image={image} graphic={graphic} priority={index === 0} />
          </div>
        ))}
      </Carousel>
    </Box>
  );
}

function HeroBackground({
  bannerImages,
  graphic,
  animationEnabled,
  autoplayInterval,
}: {
  bannerImages: BannerWrapperDathaconsImage[];
  graphic: boolean;
  animationEnabled: boolean;
  autoplayInterval: number;
}) {
  const primary = bannerImages[0];
  const [carouselReady, setCarouselReady] = useState(false);
  const handleCarouselReady = useCallback(() => setCarouselReady(true), []);

  if (!primary) return null;

  const useCarousel = animationEnabled && bannerImages.length > 1;
  const showFallback = !useCarousel || !carouselReady;

  return (
    <div className="absolute inset-0 size-full overflow-hidden bg-[#1a0033]" data-name="Hero image">
      {showFallback && (
        <div className="absolute inset-0 z-0 size-full overflow-hidden">
          <HeroBannerImage image={primary} priority />
        </div>
      )}
      {useCarousel && (
        <HeroCarousel
          images={bannerImages}
          graphic={graphic}
          animationEnabled={animationEnabled}
          autoplayInterval={autoplayInterval}
          onReady={handleCarouselReady}
        />
      )}
    </div>
  );
}

export default function BannerWrapperDathacons({
  imgSrc,
  images,
  title,
  subtitle,
  features = [],
  animationEnabled = true,
  autoplayInterval = 5000,
}: BannerWrapperDathaconsProps) {
  const bannerImages = (images?.length ? images : imgSrc ? [imgSrc] : [])
    .filter(Boolean)
    .map(normalizeHeroImage);
  const list = features.slice(0, 4);
  const isGraphicBanner = Boolean(images?.length);

  return (
    <div
      className="content-stretch flex flex-col items-center relative shrink-0 w-full"
      data-name="Hero section"
      id="hero"
      style={{ paddingBottom: list.length > 0 ? HERO_CARD_OVERLAP_PX : 0 }}
    >
      <div
        className="relative w-full overflow-hidden shrink-0 bg-[#1a0033]"
        data-name="Hero"
        {...(isGraphicBanner ? { "data-graphic-banner": "true" } : {})}
        style={{
          ...(isGraphicBanner ? graphicHeroFrameStyle : { height: 440 }),
          marginBottom: list.length > 0 ? -HERO_CARD_OVERLAP_PX : 0,
          position: "relative",
        }}
      >
        {bannerImages.length > 0 && (
          <HeroBackground
            bannerImages={bannerImages}
            graphic={isGraphicBanner}
            animationEnabled={animationEnabled}
            autoplayInterval={autoplayInterval}
          />
        )}
        {!isGraphicBanner && (
          <div aria-hidden className="absolute bg-[rgba(0,0,0,0.22)] inset-0 z-[1]" />
        )}
        {(title || subtitle) && !isGraphicBanner && (
          <div
            className="absolute content-stretch flex flex-col gap-[12px] items-start leading-[normal] left-1/2 -translate-x-1/2 not-italic text-white top-[148px] w-[min(1120px,calc(100%-40px))] px-5 z-[2]"
            data-name="Hero text Container max-width 1120px"
          >
            {title && (
              <p className="font-black min-w-0 relative shrink-0 text-[clamp(35px,6vw,53px)] w-full max-w-[20ch] md:text-[53px]">
                {title}
              </p>
            )}
            {subtitle && (
              <p className="font-medium relative shrink-0 text-[17px] text-white/95 max-w-xl whitespace-normal sm:text-[18px] md:text-[19px] sm:whitespace-nowrap">
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {list.length > 0 && (
        <div
          className="content-stretch flex flex-wrap sm:flex-nowrap justify-center gap-[clamp(12px,2vw,30px)] items-stretch sm:items-center relative shrink-0 w-full max-w-[1120px] px-5 z-[2]"
          data-name="Icon section Container max-width 1120px"
          style={{ marginBottom: -HERO_CARD_OVERLAP_PX }}
        >
          {list.map((f, i) => (
            <ValueCard
              key={`${f.label}-${i}`}
              label={f.label}
              icon={f.icon}
              widthClass={cardWidths[i] ?? "w-[258px]"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

BannerWrapperDathacons.displayName = "BannerWrapperDathacons";
